import { useFeatureFlags } from "./use-feature-flags";
import { useScope } from "@/lib/scope-context";

/**
 * Task #2208 — PTW-Only Tenant Mode.
 *
 * Returns true when the tenant has only the PTW module enabled and all
 * cross-module dependencies (workforce, equipment, access / gate management,
 * incidents) are disabled.  The hook is intentionally conservative: if the
 * feature-flag catalog hasn't loaded yet it returns false so callers default
 * to the full-featured experience.
 *
 * Task #3797 — PTW-Only Tenant Standalone Sites & Zones.
 * Added `hasSites` flag: true when there is at least one registered worksite,
 * false for PTW-only tenants with zero real sites. Defaults to `true` while
 * the scope context is still loading so callers show the standard site/area
 * selectors by default (safe fallback). When `hasSites` is false the wizard
 * switches to the manual-location selector.
 *
 * Consumers should call this hook at the top of a component and branch on the
 * returned flags rather than calling useFeatureFlags themselves so that the
 * detection logic stays in one place.
 */
export function usePtwOnlyMode() {
  const { isEnabled, isLoading } = useFeatureFlags();
  const { worksites, isLoading: scopeLoading } = useScope();

  if (isLoading) {
    return {
      isPtwOnly: false,
      hasWorkforce: true,
      hasEquipment: true,
      hasAccess: true,
      hasIncidents: true,
      hasSites: true,
    };
  }

  const hasPtw = isEnabled("operations.ptw.basicPermits");
  const hasWorkforce = isEnabled("workforce.workers");
  const hasEquipment = isEnabled("operations.equipment");
  const hasAccess = isEnabled("access.gateManagement");
  const hasIncidents = isEnabled("safety.incidents");

  const isPtwOnly =
    hasPtw &&
    !hasWorkforce &&
    !hasEquipment &&
    !hasAccess &&
    !hasIncidents;

  // hasSites: true while scope is loading (conservative default), then derive
  // from the actual worksite count. PTW-only tenants with no registered
  // project/sites have worksites.length === 0.
  const hasSites = scopeLoading ? true : worksites.length > 0;

  return {
    isPtwOnly,
    hasWorkforce,
    hasEquipment,
    hasAccess,
    hasIncidents,
    hasSites,
  };
}
